import { ReactNode, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Menu, X, Moon, Sun } from "lucide-react";
import { usePreferencesStore } from "@/stores/preferences/preferences-provider";
import { updateThemeMode } from "@/lib/theme-utils";
import { useSession } from "@/hooks/use-session";
import { hasAuthCookie } from "@/lib/auth-utils";
import { SiteLink } from "./SiteLink";

interface SiteNavbarProps {
  /**
   * Content to render on the right side of the navbar.
   * If not provided, defaults to theme toggle + login + CTA.
   */
  rightContent?: ReactNode;
  /**
   * Variant for background color in dark mode.
   * - "homepage": Uses pure black (bg-black/80)
   * - "standard": Uses near-black zinc (bg-zinc-950/80)
   */
  variant?: "homepage" | "standard";
  /**
   * Positioning mode.
   * - "sticky" (default): sticks to top within its scroll container
   * - "fixed": always fixed to viewport top (use when the navbar's
   *   container is shorter than the page, e.g. the blog two-root layout)
   */
  position?: "sticky" | "fixed";
  /**
   * Optional sidebar toggle button (for docs page).
   * Rendered on the far left before the logo.
   */
  sidebarToggle?: ReactNode;
  /**
   * Additional class names for the navbar container.
   */
  className?: string;
}

/**
 * Navigation links shown in the navbar.
 */
const navLinks = [
  { href: "/docs", label: "Docs" },
  { href: "/blog", label: "Blog" },
];

/**
 * Default right-side content: Theme toggle + Login/Go to app + CTA
 */
function DefaultRightContent() {
  const { login } = useSession();
  const isAuthenticated = hasAuthCookie();
  const themeMode = usePreferencesStore((state) => state.themeMode);
  const setThemeMode = usePreferencesStore((state) => state.setThemeMode);

  const toggleTheme = () => {
    const newMode = themeMode === "dark" ? "light" : "dark";
    setThemeMode(newMode);
    updateThemeMode(newMode);
  };

  return (
    <div className="flex items-center gap-3">
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className="text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
      >
        {themeMode === "dark" ? (
          <Moon className="h-5 w-5" />
        ) : (
          <Sun className="h-5 w-5" />
        )}
      </Button>
      {isAuthenticated ? (
        <Button
          asChild
          variant="ghost"
          className="text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:text-black dark:hover:text-white"
        >
          <a href="/">Go to app</a>
        </Button>
      ) : (
        <Button
          variant="ghost"
          onClick={() => login()}
          className="text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:text-black dark:hover:text-white"
        >
          Log in
        </Button>
      )}
      <Button
        asChild
        size="sm"
        className="text-sm font-medium bg-zinc-900 hover:bg-zinc-800 dark:bg-white dark:hover:bg-zinc-100 dark:text-zinc-900 text-white"
      >
        <SiteLink href="/docs">Build a flow</SiteLink>
      </Button>
    </div>
  );
}

/**
 * Theme toggle button component (exportable for use in custom rightContent)
 */
export function ThemeToggle({ className }: { className?: string }) {
  const themeMode = usePreferencesStore((state) => state.themeMode);
  const setThemeMode = usePreferencesStore((state) => state.setThemeMode);

  const toggleTheme = () => {
    const newMode = themeMode === "dark" ? "light" : "dark";
    setThemeMode(newMode);
    updateThemeMode(newMode);
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggleTheme}
      className={cn(
        "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100",
        className,
      )}
    >
      {themeMode === "dark" ? (
        <Moon className="h-5 w-5" />
      ) : (
        <Sun className="h-5 w-5" />
      )}
    </Button>
  );
}

/**
 * Shared site navbar component used across all pages.
 * Provides consistent navigation with customizable right-side content.
 */
export function SiteNavbar({
  rightContent,
  variant = "standard",
  position = "sticky",
  sidebarToggle,
  className,
}: SiteNavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Background classes based on variant
  const bgClasses =
    variant === "homepage"
      ? "bg-white/80 dark:bg-black/80"
      : "bg-white/80 dark:bg-zinc-950/80";

  // Position classes: fixed needs inset-x-0 to span full width
  const positionClasses =
    position === "fixed" ? "fixed inset-x-0 top-0" : "sticky top-0";

  return (
    <nav
      className={cn(
        positionClasses,
        "z-50 backdrop-blur-lg border-b border-zinc-200 dark:border-zinc-800",
        bgClasses,
        className,
      )}
    >
      <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
        {/* Left side: Optional sidebar toggle + Logo + Nav links */}
        <div className="flex items-center gap-6">
          {/* Sidebar toggle (for docs) */}
          {sidebarToggle}

          {/* Logo - uses CSS to switch between light/dark versions for instant render.
              width/height attributes provide intrinsic aspect-ratio so the browser
              reserves the correct space before the image loads (prevents CLS). */}
          <a href="/" className="shrink-0">
            <img
              src="/taias_light.png"
              alt="Taias"
              width={101}
              height={32}
              className="h-8 w-auto dark:hidden"
            />
            <img
              src="/taias_dark.png"
              alt="Taias"
              width={101}
              height={32}
              className="h-8 w-auto hidden dark:block"
            />
          </a>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Button
                key={link.href}
                asChild
                variant="ghost"
                className="text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:text-black dark:hover:text-white transition-none"
              >
                <SiteLink href={link.href}>{link.label}</SiteLink>
              </Button>
            ))}
          </div>
        </div>

        {/* Right side: Custom content or default */}
        <div className="hidden md:flex items-center">
          {rightContent ?? <DefaultRightContent />}
        </div>

        {/* Mobile menu button */}
        <div className="flex md:hidden items-center gap-2">
          {/* Show right content on mobile too if it's simple (like theme toggle) */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-zinc-600 dark:text-zinc-400"
          >
            {mobileMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </Button>
        </div>
      </div>

      {/* Mobile menu dropdown */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-zinc-200 dark:border-zinc-800">
          <div className="mx-auto max-w-7xl px-6 py-4 space-y-4">
            {/* Nav links */}
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <SiteLink
                  key={link.href}
                  href={link.href}
                  className="text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:text-black dark:hover:text-white py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </SiteLink>
              ))}
            </div>
            {/* Right content in mobile menu */}
            <div className="pt-4 border-t border-zinc-200 dark:border-zinc-800">
              {rightContent ?? <DefaultRightContent />}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

export default SiteNavbar;
